import {
  buildChartToolDescription,
  findChartCapability,
} from "./chartCapabilityRegistry";
import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import { FACTSET_CHART_PARAMETERS_SCHEMA } from "./chartCreatorTools";
import {
  matchChartCreatorTemplate,
  type ChartCreatorTemplateFingerprint,
} from "./chartCreatorTemplateMap";
import {
  SUMMARY_TABLE_DEFAULT_DISPLAY,
  SUMMARY_TABLE_HIDDEN,
  SUMMARY_TABLE_PERIOD_SLOTS,
  applyRelativeSlotEdit,
  defaultSummaryTablePeriods,
  isSummaryTableSlotIndex,
} from "./chartCreatorSummaryTable";
import { assertChartParametersValid } from "./chartCreatorPayload";
import {
  resolveChartCreatorMode,
  runOrJoinChartCreatorReauth,
  type ChartCreatorModeResolution,
} from "./chartCreatorMode";
import {
  activateWorksheet,
  addWorkbook,
  downloadExcelChart,
  getActiveExcelContext,
  getCalculationMode,
  getWorkbookNamedObjects,
  insertWorksheetsFromBase64,
  setCalculationMode,
} from "./excelHostOps";
import {
  listWorksheetsMetadata,
  readRangeContent,
  type ReadRangeContentResult,
  type WorksheetMetadata,
} from "./excelService";
import {
  downloadExcelChartViaApi,
} from "./services/factset/chartCreatorApi";
import {
  drainEditModeAfterWrite,
  waitForHostReadyPatient,
  type PatientHostReadyResult,
} from "./services/hostReady";
import {
  getChartsViaIntents,
  type ActiveExcelContext,
  type CalculationMode,
  type ChartingChartInfo,
  type GetChartsOptions,
} from "./services/factset/spreadsheetIntents";
import {
  nativeChartWorkbookRecords,
  type NativeChartWorkbookRecordStore,
} from "./nativeChartWorkbookRecords";
import type {
  FactsetChartParameters,
  FactsetChartSummaryTable,
  FactsetChartSummaryTableDisplay,
  FactsetChartSummaryTablePeriod,
  FactsetDownloadExcelChart,
  WorkbookNamedObject,
} from "./types";
import { FACTSET_CHART_SUMMARY_TABLE_PERIODS } from "./types";
import { isIntentTimeoutError } from "../shared/factset/factsetIntentTransport";
import {
  asRecord,
  buildNamedObjectScopeIndex,
  cellAddress,
  parseCellCoordinates,
  parseSingleCellReference,
  pickString,
} from "./workbookCellReferences";
import { resetWorkbookScopedRunAccumulators } from "./executorAccumulators";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import {
  beginChartStoppingEvidence,
  finishChartStoppingEvidence,
} from "./executorChartStopping";

const NATIVE_CHART_INSERT_TIMEOUT_MS = 5 * 60_000;
const NATIVE_CHART_CREATE_VERIFY_TIMEOUT_MS = 5_000;
const NATIVE_CHART_REFRESH_ATTEMPTS = 2;
const NATIVE_CHART_REFRESH_RETRY_DELAY_MS = 750;
/**
 * `HarmonizedRefreshWorksheets` is documented active-workbook-only, and
 * `foregroundCoordinator.activate()` is best-effort — a failed activation is
 * logged, not thrown. So before dispatching a refresh we re-read the active
 * workbook and re-activate until it matches the pin, rather than assuming.
 */
const NATIVE_CHART_ACTIVATION_ATTEMPTS = 4;
const NATIVE_CHART_ACTIVATION_RETRY_DELAY_MS = 400;
/**
 * `AddWorkbook` returns before Excel finishes bringing the new workbook to the
 * foreground, and on a cold host that can take several seconds — hence a much
 * longer budget here (10s) than for re-activating an already-open workbook.
 */
const NATIVE_CHART_WORKBOOK_SWITCH_ATTEMPTS = 40;
const NATIVE_CHART_WORKBOOK_SWITCH_POLL_MS = 250;

const SUMMARY_PERIOD_SCHEMA = {
  type: ["string", "null"],
  enum: [...FACTSET_CHART_SUMMARY_TABLE_PERIODS, null],
} as const;

type NativeChartOperation =
  | "create_native_chart"
  | "read_native_chart"
  | "update_native_chart";

export interface NativeChartInputLocation {
  parameter: string;
  source: "template_fingerprint" | "named_object" | "visible_label";
  observedName: string;
  sheetName: string;
  address: string;
  currentValue: string | number | boolean | null;
}

export interface NativeChartInputMap {
  inputs: Record<string, NativeChartInputLocation>;
  ambiguousInputs: Array<{
    parameter: string;
    candidates: NativeChartInputLocation[];
  }>;
  truncatedSheets: string[];
  namedObjectDiscovery: {
    status: "completed" | "timed_out" | "failed";
    error?: string;
  };
}

export interface NativeChartDependencies {
  resolveMode: () => Promise<ChartCreatorModeResolution>;
  downloadViaApi: (
    parameters: FactsetChartParameters,
    options: { signal?: AbortSignal },
  ) => Promise<FactsetDownloadExcelChart>;
  downloadViaLegacy: (
    parameters: FactsetChartParameters,
    options: { signal?: AbortSignal },
  ) => Promise<FactsetDownloadExcelChart>;
  reauthorize: () => Promise<void>;
  createBlankWorkbook: () => Promise<unknown>;
  insertDownloadedWorkbook: (
    base64: string,
    workbookName?: string,
  ) => Promise<string[]>;
  activateSheet: (sheetName: string, workbookName: string) => Promise<unknown>;
  drainEditMode: () => Promise<unknown>;
  waitForHostReady: (signal?: AbortSignal) => Promise<PatientHostReadyResult>;
  getActiveContext: () => Promise<ActiveExcelContext>;
  getCalculationMode: () => Promise<CalculationMode>;
  setCalculationMode: (mode: CalculationMode) => Promise<unknown>;
  listSheets: (workbookName?: string) => Promise<WorksheetMetadata[]>;
  getNamedObjects: (workbookName: string) => Promise<WorkbookNamedObject[]>;
  readRange: (
    sheetName: string,
    address: string | null,
    workbookName?: string,
  ) => Promise<ReadRangeContentResult>;
  getCharts: (
    options?: GetChartsOptions,
    timeoutMs?: number,
  ) => Promise<ChartingChartInfo[]>;
  sleep: (ms: number) => Promise<void>;
  /** Creation parameters per workbook, kept beyond the current executor run. */
  records: NativeChartWorkbookRecordStore;
}

const SUMMARY_SCHEMA = {
  type: ["object", "null"],
  additionalProperties: false,
  properties: {
    display: {
      type: ["string", "null"],
      enum: ["Horizontal", "Vertical", "No", null],
    },
    period1: SUMMARY_PERIOD_SCHEMA,
    period2: SUMMARY_PERIOD_SCHEMA,
    period3: SUMMARY_PERIOD_SCHEMA,
    period4: SUMMARY_PERIOD_SCHEMA,
    period5: SUMMARY_PERIOD_SCHEMA,
    periods: {
      anyOf: [
        {
          type: "array",
          maxItems: 5,
          items: SUMMARY_PERIOD_SCHEMA,
        },
        { type: "null" },
      ],
    },
    swapPeriods: {
      anyOf: [
        {
          type: "array",
          minItems: 2,
          maxItems: 2,
          items: { type: "integer", minimum: 1, maximum: 5 },
        },
        { type: "null" },
      ],
    },
    removePeriods: {
      anyOf: [
        {
          type: "array",
          items: { type: "integer", minimum: 1, maximum: 5 },
        },
        { type: "null" },
      ],
    },
    compactPeriods: {
      type: ["boolean", "null"],
      description:
        "With removePeriods, move the surviving periods up instead of leaving the removed rows blank.",
    },
  },
} as const;

export const NATIVE_CHART_TOOL: ChartCapabilityToolDef = {
  name: "native_chart",
  description:
    `${buildChartToolDescription("native_chart")} ` +
    "Operations are exactly create_native_chart, read_native_chart, and update_native_chart. " +
    "Both creates and updates download, open, hand off this conversation to, refresh, recalculate, and verify a complete new Chart Creator workbook: " +
    "an update re-requests the chart from FactSet with the change merged into the parameters it was created with, so the previous workbook keeps its old chart, " +
    "manual worksheet tweaks are not carried over, and the new workbook is the one to keep working in. " +
    "A summary table is stated on the download request; FactSet is still rolling out support for that field, " +
    "so report a table as requested rather than as confirmed, and never describe a table you have not read.",
  inputSchema: {
    type: "object",
    properties: {
      operation: {
        type: "string",
        enum: [
          "create_native_chart",
          "read_native_chart",
          "update_native_chart",
        ],
      },
      stepId: {
        type: ["string", "null"],
        description:
          "Required for create/update. Stable id of the chart step in the current run plan.",
      },
      chartParameters: {
        anyOf: [FACTSET_CHART_PARAMETERS_SCHEMA, { type: "null" }],
        description:
          "Required for create_native_chart. Use the top-level summaryTable property for summary-table settings.",
      },
      updates: {
        anyOf: [
          {
            type: "object",
            additionalProperties: true,
            description:
              "Parameter changes for update_native_chart, as a partial chartParameters object. " +
              "They are merged into the parameters the chart was created with and the whole chart " +
              "is rebuilt in a new workbook, so send only what changes. For company-list edits send " +
              "the complete tickers array and a same-length companyNames.single array.",
          },
          { type: "null" },
        ],
      },
      summaryTable: {
        ...SUMMARY_SCHEMA,
        description:
          "Summary-table settings; periods can be set outright, swapped, or removed. " +
          "Send only what the user named explicitly: " +
          '"with a vertical summary table" is a display, "showing Current, L1Y and L5Y" ' +
          "are periods, and \"swap the last two periods\" is a rearrangement. Omit the " +
          "field when the user named neither, including for a plain chart request and for " +
          'a bare "add a summary table": the applicable chart types already include a ' +
          "horizontal table, so there is nothing to override.",
      },
    },
    required: [
      "operation",
      "stepId",
      "chartParameters",
      "updates",
      "summaryTable",
    ],
    additionalProperties: false,
  },
};

export const EXECUTOR_NATIVE_CHART_TOOLS: readonly ChartCapabilityToolDef[] = [
  NATIVE_CHART_TOOL,
];

const PARAMETER_ALIASES: Record<string, readonly string[]> = {
  chartType: ["charttype", "chartcode", "graphtype"],
  tickers: ["tickers", "ticker", "identifiers", "securities", "entities"],
  "companyNames.single": ["companyname", "companynamesingle", "singlecompanyname"],
  "companyNames.aggregated": [
    "companynames",
    "companynamesaggregated",
    "aggregatedcompanynames",
  ],
  frequency: ["frequency", "chartfrequency"],
  startDate: ["startdate", "fromdate"],
  endDate: ["enddate", "todate"],
  horizon: ["horizon", "rollinghorizon"],
  annotations: ["annotations", "annotation"],
  stylesetId: ["styleset", "stylesetid", "style"],
  mathFunction: ["mathfunction", "mathematicalfunction"],
  quickLabel: ["quicklabel", "quicklabels"],
  currencies: ["currencies", "currency"],
  // Deliberately not aliased to a bare "period": templates use that word for
  // the chart's own range and for summary-table slots, and a wrong-cell write
  // is worse than falling back to a rebuild.
  "estimateParameters.period": ["estimateperiod"],
  "estimateParameters.estimatePeriods": [
    "estimateperiods",
    "numberofestimateperiods",
  ],
  "estimateParameters.actualHistory": [
    "actualhistory",
    "actualhistoryperiods",
  ],
  "chartFormat.title": ["charttitle", "title"],
  "chartFormat.subtitle": ["chartsubtitle", "subtitle"],
  "chartFormat.xAxisTitle": ["xaxistitle", "horizontalaxistitle"],
  "chartFormat.yAxisTitle": ["yaxistitle", "verticalaxistitle"],
  "chartFormat.showLegend": ["showlegend", "legend"],
  "chartFormat.legendPosition": ["legendposition"],
  "summaryTable.display": [
    "summarytable",
    "summarytabledisplay",
    "summarytablelayout",
    "summarylayout",
  ],
  "summaryTable.period1": ["summaryperiod1", "period1"],
  "summaryTable.period2": ["summaryperiod2", "period2"],
  "summaryTable.period3": ["summaryperiod3", "period3"],
  "summaryTable.period4": ["summaryperiod4", "period4"],
  "summaryTable.period5": ["summaryperiod5", "period5"],
};

/**
 * True when the download request has no field for this parameter, so a rebuild
 * cannot carry it — `chartFormat` styles a title but never sets one, for
 * instance. An update reports these as left alone instead of pretending to
 * change them.
 *
 * Asks the schema that validates the request rather than keeping a second list
 * beside it: a nested object that closes itself to extra properties has told
 * us exactly which leaves it accepts. Paths whose parent the schema does not
 * describe at all (`summaryTable.*`, resolved separately) are left to their own
 * handling.
 */
function isWorksheetOnlyParameter(path: string): boolean {
  const separator = path.indexOf(".");
  if (separator < 0) return false;
  const parent = asRecord(
    asRecord(FACTSET_CHART_PARAMETERS_SCHEMA.properties)?.[
      path.slice(0, separator)
    ],
  );
  if (!parent || parent.additionalProperties !== false) return false;
  const accepted = asRecord(parent.properties);
  return !accepted || !(path.slice(separator + 1) in accepted);
}

// First declaration wins, so an alias shared by two parameters resolves to the
// one listed first rather than to whichever happens to be enumerated last.
const ALIAS_TO_PARAMETER = new Map<string, string>();
for (const [parameter, aliases] of Object.entries(PARAMETER_ALIASES)) {
  for (const alias of aliases) {
    if (!ALIAS_TO_PARAMETER.has(alias)) ALIAS_TO_PARAMETER.set(alias, parameter);
  }
}

/**
 * Words template authors wrap an input's real name in — `Input_Start_Date`,
 * `StartDateCell`, `ccChartTitle`. Dropped as whole words only, and only after
 * the undecorated lookup misses, so a name that already denotes a parameter is
 * never rewritten into a different one and a word that merely begins with one
 * of these (`Charting`) is left alone.
 */
const DECORATION_WORDS = new Set([
  "cc",
  "cell",
  "chart",
  "chartcreator",
  "creator",
  "field",
  "inp",
  "input",
  "inputs",
  "param",
  "parameter",
  "parameters",
  "setting",
  "settings",
  "value",
]);

/**
 * Split an observed name into its words. Separators and case transitions are
 * the only signals available: `Start Date`, `start_date`, and `startDate` all
 * have to reduce to the same two words.
 */
function observedNameWords(value: string): string[] {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter((word) => word.length > 0)
    .map((word) => word.toLowerCase());
}

function parameterForObservedName(value: string): string | undefined {
  const words = observedNameWords(value);
  if (words.length === 0) return undefined;
  const direct = ALIAS_TO_PARAMETER.get(words.join(""));
  if (direct) return direct;
  const undecorated = words.filter((word) => !DECORATION_WORDS.has(word));
  if (undecorated.length === 0 || undecorated.length === words.length) {
    return undefined;
  }
  return ALIAS_TO_PARAMETER.get(undecorated.join(""));
}

/** A cell holds an input value if it is neither empty nor absent. */
function hasCellValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== "";
}

function valueAt(
  reads: Map<string, ReadRangeContentResult>,
  sheetName: string,
  address: string,
): string | number | boolean | null {
  const range = reads.get(sheetName.toLowerCase())?.range;
  if (!range) return null;
  const cell = parseCellCoordinates(address);
  if (!cell) return null;
  const rowOffset = cell.row - range.startRow;
  const columnOffset = cell.column - range.startColumn;
  return range.values[rowOffset]?.[columnOffset] ?? null;
}

export async function discoverNativeChartInputs(
  workbookName: string,
  dependencies: Pick<
    NativeChartDependencies,
    "listSheets" | "getNamedObjects" | "readRange"
  >,
): Promise<{
  sheets: WorksheetMetadata[];
  inputMap: NativeChartInputMap;
  namedObjectNames: string[];
  reads: Map<string, ReadRangeContentResult>;
}> {
  const sheets = await dependencies.listSheets(workbookName);
  const reads = new Map<string, ReadRangeContentResult>();
  const truncatedSheets: string[] = [];
  for (const sheet of sheets) {
    if (!sheet.usedRange) continue;
    const read = await dependencies.readRange(
      sheet.name,
      sheet.usedRange.address,
      workbookName,
    );
    reads.set(sheet.name.toLowerCase(), read);
    if (read.truncated) truncatedSheets.push(sheet.name);
  }

  const candidates = new Map<string, NativeChartInputLocation[]>();
  const addCandidate = (candidate: NativeChartInputLocation) => {
    const key = `${candidate.sheetName.toLowerCase()}!${candidate.address.toUpperCase()}`;
    const current = candidates.get(candidate.parameter) ?? [];
    if (!current.some((entry) =>
      `${entry.sheetName.toLowerCase()}!${entry.address.toUpperCase()}` === key
    )) {
      current.push(candidate);
      candidates.set(candidate.parameter, current);
    }
  };

  let namedObjects: WorkbookNamedObject[] = [];
  let namedObjectDiscovery: NativeChartInputMap["namedObjectDiscovery"] = {
    status: "completed",
  };
  try {
    namedObjects = await dependencies.getNamedObjects(workbookName);
  } catch (error) {
    namedObjectDiscovery = {
      status: isIntentTimeoutError(error) ? "timed_out" : "failed",
      error: errorText(error),
    };
  }
  const scopeIndex = buildNamedObjectScopeIndex(sheets);
  const namedObjectNames: string[] = [];
  for (const object of namedObjects) {
    const record = asRecord(object);
    if (!record) continue;
    const observedName = pickString(record, [
      "name",
      "displayName",
      "localName",
      "Name",
    ]);
    if (observedName) namedObjectNames.push(observedName);
    const parameter = observedName
      ? parameterForObservedName(observedName)
      : undefined;
    if (!observedName || !parameter) continue;
    const referenceContext = {
      scope: record.scope ?? record.Scope,
      scopeIndex,
    };
    const reference =
      parseSingleCellReference(record.address, referenceContext) ??
      parseSingleCellReference(record.rangeAddress, referenceContext) ??
      parseSingleCellReference(record.refersTo, referenceContext) ??
      parseSingleCellReference(record.reference, referenceContext) ??
      parseSingleCellReference(record.formula, referenceContext);
    if (!reference) continue;
    addCandidate({
      parameter,
      source: "named_object",
      observedName,
      sheetName: reference.sheetName,
      address: reference.address,
      currentValue: valueAt(
        reads,
        reference.sheetName,
        reference.address,
      ),
    });
  }

  for (const sheet of sheets) {
    if (sheet.visibility && sheet.visibility !== "Visible") continue;
    const range = reads.get(sheet.name.toLowerCase())?.range;
    if (!range) continue;
    for (let row = 0; row < range.values.length; row++) {
      const values = range.values[row] ?? [];
      for (let column = 0; column < values.length; column++) {
        const label = values[column];
        if (typeof label !== "string" || !label.trim()) continue;
        const parameter = parameterForObservedName(label);
        if (!parameter) continue;
        const rightExists = column + 1 < values.length;
        const rightValue = rightExists ? values[column + 1] : undefined;
        const belowValue = range.values[row + 1]?.[column];
        const rightIsAnotherLabel =
          typeof rightValue === "string" &&
          parameterForObservedName(rightValue) !== undefined;
        const belowIsAnotherLabel =
          typeof belowValue === "string" &&
          parameterForObservedName(belowValue) !== undefined;
        const rightPopulated = rightExists && hasCellValue(rightValue);
        const belowPopulated = hasCellValue(belowValue);
        const useBelow =
          !belowIsAnotherLabel &&
          belowPopulated &&
          (!rightExists || !rightPopulated || rightIsAnotherLabel);
        if (!useBelow && (!rightExists || rightIsAnotherLabel)) continue;
        addCandidate({
          parameter,
          source: "visible_label",
          observedName: label.trim(),
          sheetName: sheet.name,
          address: useBelow
            ? cellAddress(
                range.startRow + row + 1,
                range.startColumn + column,
              )
            : cellAddress(
                range.startRow + row,
                range.startColumn + column + 1,
              ),
          currentValue: (useBelow ? belowValue : rightValue) ?? null,
        });
      }
    }
  }

  const inputs: Record<string, NativeChartInputLocation> = {};
  const ambiguousInputs: NativeChartInputMap["ambiguousInputs"] = [];
  candidates.forEach((entries, parameter) => {
    const namedEntries = entries.filter(
      (entry) => entry.source === "named_object",
    );
    const authoritative = namedEntries.length > 0 ? namedEntries : entries;
    if (authoritative.length === 1) inputs[parameter] = authoritative[0];
    else ambiguousInputs.push({ parameter, candidates: authoritative });
  });
  return {
    sheets,
    namedObjectNames,
    reads,
    inputMap: {
      inputs,
      ambiguousInputs,
      truncatedSheets,
      namedObjectDiscovery,
    },
  };
}

export interface ResolvedNativeChartInputs {
  sheets: WorksheetMetadata[];
  inputMap: NativeChartInputMap;
  fingerprint?: ChartCreatorTemplateFingerprint;
  /** Where each resolved parameter came from, for the completion evidence. */
  resolutionSource: "template_fingerprint" | "discovery" | "mixed";
}

/**
 * Place Chart Creator inputs, preferring a confirmed template fingerprint over
 * inferred discovery. A fingerprint match is authoritative: it was derived
 * from real bytes, so it overrides a label-adjacency guess for the same
 * parameter and clears any ambiguity recorded for it.
 */
async function resolveNativeChartInputs(
  workbookName: string,
  dependencies: Pick<
    NativeChartDependencies,
    "listSheets" | "getNamedObjects" | "readRange"
  >,
): Promise<ResolvedNativeChartInputs> {
  const discovered = await discoverNativeChartInputs(workbookName, dependencies);
  const fingerprint = matchChartCreatorTemplate({
    sheetNames: discovered.sheets.map((sheet) => sheet.name),
    namedObjectNames: discovered.namedObjectNames,
  });
  if (!fingerprint) {
    return {
      sheets: discovered.sheets,
      inputMap: discovered.inputMap,
      resolutionSource: "discovery",
    };
  }

  const inputs: Record<string, NativeChartInputLocation> = {
    ...discovered.inputMap.inputs,
  };
  for (const [parameter, cell] of Object.entries(fingerprint.inputs)) {
    inputs[parameter] = {
      parameter,
      source: "template_fingerprint",
      observedName: `${fingerprint.version}:${parameter}`,
      sheetName: cell.sheet,
      address: cell.address,
      currentValue: valueAt(discovered.reads, cell.sheet, cell.address),
    };
  }
  // `ModifyShapes` can only move and resize, so the layout combo box is only
  // drivable when the probe found the cell it is linked to.
  const layoutCell = fingerprint.layoutLinkedCell;
  if (layoutCell && !inputs["summaryTable.display"]) {
    inputs["summaryTable.display"] = {
      parameter: "summaryTable.display",
      source: "template_fingerprint",
      observedName: `${fingerprint.version}:layoutLinkedCell`,
      sheetName: layoutCell.sheet,
      address: layoutCell.address,
      currentValue: valueAt(discovered.reads, layoutCell.sheet, layoutCell.address),
    };
  }
  const ambiguousInputs = discovered.inputMap.ambiguousInputs.filter(
    (entry) => !Object.prototype.hasOwnProperty.call(fingerprint.inputs, entry.parameter),
  );
  const onlyFingerprint = Object.keys(inputs).every((parameter) =>
    Object.prototype.hasOwnProperty.call(fingerprint.inputs, parameter),
  );
  return {
    sheets: discovered.sheets,
    fingerprint,
    resolutionSource: onlyFingerprint ? "template_fingerprint" : "mixed",
    inputMap: {
      ...discovered.inputMap,
      inputs,
      ambiguousInputs,
    },
  };
}

export const DEFAULT_NATIVE_CHART_DEPENDENCIES: NativeChartDependencies = {
  resolveMode: () => resolveChartCreatorMode(),
  downloadViaApi: (parameters, options) =>
    downloadExcelChartViaApi(parameters, options),
  downloadViaLegacy: (parameters, options) =>
    downloadExcelChart(parameters, {
      signal: options.signal,
      isEdit: true,
      generatePreview: "None",
    }),
  reauthorize: runOrJoinChartCreatorReauth,
  createBlankWorkbook: () => addWorkbook(),
  insertDownloadedWorkbook: (base64, workbookName) =>
    insertWorksheetsFromBase64(
      base64,
      workbookName ? { workbookName } : undefined,
      NATIVE_CHART_INSERT_TIMEOUT_MS,
    ),
  activateSheet: (sheetName, workbookName) =>
    activateWorksheet(sheetName, { workbookName }),
  drainEditMode: () => drainEditModeAfterWrite(),
  waitForHostReady: (signal) => waitForHostReadyPatient({ signal }),
  getActiveContext: getActiveExcelContext,
  getCalculationMode,
  setCalculationMode,
  listSheets: listWorksheetsMetadata,
  getNamedObjects: (workbookName) =>
    getWorkbookNamedObjects({
      workbookName,
      skipHidden: false,
      properties: [
        "name",
        "displayName",
        "address",
        "rangeAddress",
        "refersTo",
        "reference",
        "formula",
        // Sheet-scoped names come back as `refersTo: "=A1"` with the owning
        // worksheet only in `scope`; without it they cannot be placed.
        "scope",
      ],
    }),
  readRange: (sheetName, address, workbookName) =>
    readRangeContent(
      sheetName,
      address,
      { includeFormulas: true, includeFormats: false },
      workbookName,
    ),
  getCharts: (options, timeoutMs) =>
    getChartsViaIntents(options, timeoutMs),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  records: nativeChartWorkbookRecords,
};

interface NativeChartToolInput {
  operation?: NativeChartOperation;
  stepId?: string | null;
  chartParameters?: FactsetChartParameters | null;
  updates?: Record<string, unknown> | null;
  summaryTable?: (FactsetChartSummaryTable & {
    periods?: Array<FactsetChartSummaryTablePeriod | null> | null;
    swapPeriods?: [number, number] | null;
    removePeriods?: number[] | null;
    compactPeriods?: boolean | null;
  }) | null;
}

type SummaryTableRequest = NonNullable<NativeChartToolInput["summaryTable"]>;

/** Does this request rearrange what is already there, rather than state it outright? */
function hasRelativeSummaryOps(summary: SummaryTableRequest): boolean {
  return Boolean(
    summary.swapPeriods ||
      (summary.removePeriods && summary.removePeriods.length > 0) ||
      summary.compactPeriods,
  );
}

/**
 * Catch slot indices the schema should have caught. Model providers do not all
 * enforce `minimum`/`maximum`, and quietly dropping an out-of-range slot would
 * report a table the user never asked for as the one they did.
 */
function summarySlotIndexError(
  summary: SummaryTableRequest,
): string | undefined {
  const offenders = [
    ...(summary.swapPeriods ?? []),
    ...(summary.removePeriods ?? []),
  ].filter((index) => !isSummaryTableSlotIndex(index));
  if (offenders.length === 0) return undefined;
  return (
    `Summary-table period slots are numbered 1-${SUMMARY_TABLE_PERIOD_SLOTS}; ` +
    `received ${offenders.join(", ")}, so the requested table was left out.`
  );
}

function output(
  value: Record<string, unknown>,
): string {
  return JSON.stringify(value);
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

class NativeChartLifecycleError extends Error {
  constructor(
    message: string,
    public readonly details: Record<string, unknown>,
  ) {
    super(message);
    this.name = "NativeChartLifecycleError";
    Object.setPrototypeOf(this, NativeChartLifecycleError.prototype);
  }
}

function phaseError(
  phase: string,
  error: unknown,
  details: Record<string, unknown> = {},
): NativeChartLifecycleError {
  return new NativeChartLifecycleError(
    `Native chart ${phase} failed: ${errorText(error)}`,
    { ...details, phase },
  );
}

function failure(
  ctx: ExecutorToolRuntimeContext,
  args: {
    operation: NativeChartOperation;
    stepId?: string;
    error: string;
    partialMutation?: boolean;
    details?: Record<string, unknown>;
  },
): string {
  if (args.stepId) {
    finishChartStoppingEvidence(ctx, {
      stepId: args.stepId,
      tool: "native_chart",
      operation: args.operation,
      satisfied: false,
      reason: args.error,
      details: args.details,
    });
  }
  return output({
    ok: false,
    applied: false,
    operation: args.operation,
    stepId: args.stepId ?? null,
    error: args.error,
    partialMutation: args.partialMutation === true,
    completionEvidence: {
      satisfied: false,
      ...(args.details ?? {}),
    },
  });
}

async function requireHostReady(
  dependencies: NativeChartDependencies,
  signal?: AbortSignal,
): Promise<PatientHostReadyResult> {
  const readiness = await dependencies.waitForHostReady(signal);
  if (!readiness.ready) {
    throw new Error(
      `Excel is not ready for the native-chart operation (${readiness.reason}).`,
    );
  }
  return readiness;
}

/**
 * Wait until Excel reports a workbook that is demonstrably not the one the user
 * was in, which is the only proof available that `AddWorkbook` landed.
 *
 * Everything downstream inserts the downloaded sheets into the workbook this
 * returns, so a wrong answer writes Chart Creator's sheets into the user's own
 * file. That makes both checks assertions rather than conveniences: the names
 * are compared on their normalized form, since a host that answers with a path
 * one poll and a bare name the next has not switched workbooks, and a run with
 * no name to compare against cannot prove a switch at all.
 */
async function waitForWorkbookSwitch(
  dependencies: NativeChartDependencies,
  previousWorkbookName: string | undefined,
  signal?: AbortSignal,
): Promise<ActiveExcelContext> {
  if (!previousWorkbookName?.trim()) {
    throw new Error(
      "Excel did not report which workbook was active before AddWorkbook, so the new workbook cannot be told apart from the one already open.",
    );
  }
  let last: ActiveExcelContext | undefined;
  for (
    let attempt = 0;
    attempt < NATIVE_CHART_WORKBOOK_SWITCH_ATTEMPTS;
    attempt++
  ) {
    if (signal?.aborted) throw new DOMException("Aborted by user", "AbortError");
    last = await dependencies.getActiveContext();
    const name = last.workbookName?.trim();
    if (name && !sameWorkbook(name, previousWorkbookName)) return last;
    await dependencies.sleep(NATIVE_CHART_WORKBOOK_SWITCH_POLL_MS);
  }
  throw new Error(
    `Excel did not activate the new workbook created by AddWorkbook${
      last?.workbookName ? `; active workbook is "${last.workbookName}"` : ""
    }.`,
  );
}

async function verifyNativeCharts(
  dependencies: NativeChartDependencies,
  workbookName: string,
  sheetName?: string,
  timeoutMs?: number,
): Promise<ChartingChartInfo[]> {
  const charts = await dependencies.getCharts(
    {
      workbookName,
      ...(sheetName ? { sheetRef: { name: sheetName } } : {}),
      type: "Chart",
      detail: "Summary",
    },
    timeoutMs,
  );
  return charts.filter((chart) => chart.type === "Chart" || chart.type === undefined);
}

/** Excel's placeholder for a cell whose value has not been calculated yet. */
const CALCULATION_PLACEHOLDER = "#CALC";

function isCalculationPlaceholder(value: unknown): boolean {
  return (
    typeof value === "string" &&
    value.toUpperCase().includes(CALCULATION_PLACEHOLDER)
  );
}

function hasCalculationPlaceholder(charts: readonly ChartingChartInfo[]): boolean {
  return charts.some(
    (chart) =>
      isCalculationPlaceholder(chart.name) ||
      isCalculationPlaceholder(chart.title?.text) ||
      chart.series?.some((series) =>
        isCalculationPlaceholder(series.name),
      ) === true,
  );
}

/**
 * Clamp a used range to a bounded top-left inspection window without assuming
 * where that range begins.
 */
function boundedRangeAddress(
  address: string,
  maxRows: number,
  maxColumns: number,
): string | undefined {
  const corners = address.split(":");
  if (corners.length > 2) return undefined;
  const start = parseCellCoordinates(corners[0]);
  const end = corners[1] ? parseCellCoordinates(corners[1]) : start;
  if (!start || !end) return undefined;
  const lowRow = Math.min(start.row, end.row);
  const lowColumn = Math.min(start.column, end.column);
  return `${cellAddress(lowRow, lowColumn)}:${cellAddress(
    Math.min(Math.max(start.row, end.row), lowRow + maxRows - 1),
    Math.min(
      Math.max(start.column, end.column),
      lowColumn + maxColumns - 1,
    ),
  )}`;
}

function resetWorkbookScopedRunState(
  ctx: ExecutorToolRuntimeContext,
  activeSheetName: string | undefined,
): void {
  if (activeSheetName?.trim()) {
    ctx.session.sessionLockSheet = activeSheetName.trim();
  }
  resetWorkbookScopedRunAccumulators(ctx.accumulators);
}

/**
 * Turn a nested parameter delta into dotted paths (`chartFormat.title`), which
 * is the vocabulary the input map, the fingerprints, and `setByPath` all
 * share. Arrays stay whole: they are values in this contract, not branches.
 */
function flattenObject(
  value: Record<string, unknown>,
  prefix = "",
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (child && typeof child === "object" && !Array.isArray(child)) {
      Object.assign(result, flattenObject(child as Record<string, unknown>, path));
    } else {
      result[path] = child;
    }
  }
  return result;
}

type SummaryTableSlotValue = string | number | boolean | null;

interface SummaryTableState {
  display: FactsetChartSummaryTableDisplay;
  periods: FactsetChartSummaryTablePeriod[];
  /**
   * The slot cells exactly as the worksheet holds them, when discovery could
   * read them. Relative edits move these rather than the coerced codes, so a
   * cell's own formatting survives a rearrangement that only reorders slots.
   */
  worksheetPeriods?: SummaryTableSlotValue[];
  /** Where the reported state came from, so callers can say how sure it is. */
  source: "worksheet" | "stored_parameters" | "documented_default" | "unknown";
}

/**
 * Worksheet spellings of "this slot is hidden". `No` is the API's own value;
 * templates surface it to the user as either word.
 */
const HIDDEN_SLOT_WORDS = new Set(["no", "none"]);

/** Is this slot showing a period, as opposed to being hidden or absent? */
function slotIsOccupied(value: unknown): boolean {
  if (typeof value === "number") return true;
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.length > 0 && !HIDDEN_SLOT_WORDS.has(trimmed.toLowerCase());
}

/** Compare slot values so "", null, and "No" all read as the same hidden slot. */
function slotComparable(value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = (typeof value === "string" ? value.trim() : String(value))
    .toLowerCase();
  return HIDDEN_SLOT_WORDS.has(text) ? "" : text;
}

function coercePeriod(value: unknown): FactsetChartSummaryTablePeriod | undefined {
  if (typeof value !== "string") return undefined;
  return FACTSET_CHART_SUMMARY_TABLE_PERIODS.find(
    (period) => period.toLowerCase() === value.trim().toLowerCase(),
  );
}

function coerceDisplay(
  value: unknown,
): FactsetChartSummaryTableDisplay | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === "horizontal") return "Horizontal";
  if (normalized === "vertical") return "Vertical";
  if (HIDDEN_SLOT_WORDS.has(normalized)) return "No";
  return undefined;
}

/**
 * Best available picture of the current summary table.
 *
 * Worksheet cells win when discovery could place them. Otherwise fall back to
 * what the workbook was created with, and finally to the documented default
 * period set for the chart's time range — the same rule the template applies,
 * so a relative edit ("swap L3M and L6M") has a real basis even when we cannot
 * read the cells.
 */
function resolveSummaryTableState(
  inputMap: NativeChartInputMap,
  storedParameters: FactsetChartParameters | undefined,
  now: Date = new Date(),
): SummaryTableState {
  const observedPeriods: FactsetChartSummaryTablePeriod[] = [];
  const worksheetPeriods: SummaryTableSlotValue[] = [];
  let observedCount = 0;
  for (let index = 1; index <= SUMMARY_TABLE_PERIOD_SLOTS; index += 1) {
    const cell = inputMap.inputs[`summaryTable.period${index}`];
    const raw = cell ? (cell.currentValue ?? null) : null;
    worksheetPeriods.push(raw);
    // Only a cell that reads as one of the API's own period codes counts as
    // having been observed. A slot holding something else — a template's own
    // wording, a placeholder, a formatted value — means the table was found but
    // not understood, and calling that "all periods hidden" would be as wrong as
    // guessing the periods outright.
    const period = coercePeriod(raw);
    if (period) observedCount += 1;
    observedPeriods.push(period ?? SUMMARY_TABLE_HIDDEN);
  }
  const observedDisplay = coerceDisplay(
    inputMap.inputs["summaryTable.display"]?.currentValue,
  );
  if (observedCount > 0) {
    return {
      display: observedDisplay ?? SUMMARY_TABLE_DEFAULT_DISPLAY,
      periods: observedPeriods,
      worksheetPeriods,
      source: "worksheet",
    };
  }

  const stored = storedParameters?.summaryTable;
  const storedPeriods: FactsetChartSummaryTablePeriod[] = [];
  let storedCount = 0;
  for (let index = 1; index <= SUMMARY_TABLE_PERIOD_SLOTS; index += 1) {
    const period = coercePeriod(
      stored?.[`period${index}` as keyof FactsetChartSummaryTable],
    );
    if (period) storedCount += 1;
    storedPeriods.push(period ?? SUMMARY_TABLE_HIDDEN);
  }
  if (storedCount > 0) {
    return {
      display:
        coerceDisplay(stored?.display) ??
        observedDisplay ??
        SUMMARY_TABLE_DEFAULT_DISPLAY,
      periods: storedPeriods,
      source: "stored_parameters",
    };
  }

  const documented = defaultSummaryTablePeriods(
    storedParameters?.startDate,
    storedParameters?.endDate,
    now,
  );
  if (documented) {
    return {
      display:
        coerceDisplay(stored?.display) ??
        observedDisplay ??
        SUMMARY_TABLE_DEFAULT_DISPLAY,
      periods: documented,
      source: "documented_default",
    };
  }
  return {
    display: observedDisplay ?? SUMMARY_TABLE_DEFAULT_DISPLAY,
    periods: observedPeriods,
    source: "unknown",
  };
}

/**
 * Turn a summary-table request into absolute `summaryTable.*` values.
 *
 * Section 19 of the parameter skill requires absolute periods on the request,
 * so a relative operation ("swap L3M and L6M") is resolved here against
 * `currentState`: the table the workbook shows, the one the remembered request
 * asked for, or the documented band for its date range.
 */
function summaryUpdates(
  summary: NativeChartToolInput["summaryTable"],
  currentState: SummaryTableState,
): Record<string, unknown> {
  if (!summary) return {};
  const result: Record<string, unknown> = {};
  // A named layout always goes out, even when it matches the layout we believe
  // the chart already has. That belief is usually the documented default rather
  // than something read from the workbook, so treating a match as "no change"
  // would swallow the one request that asks for the standard table.
  const display = coerceDisplay(summary.display);
  if (display) result["summaryTable.display"] = display;

  const before: SummaryTableSlotValue[] =
    currentState.worksheetPeriods ?? [...currentState.periods];
  let after: SummaryTableSlotValue[] | undefined;
  if (Array.isArray(summary.periods)) {
    after = [];
    for (let index = 0; index < SUMMARY_TABLE_PERIOD_SLOTS; index += 1) {
      after.push(coercePeriod(summary.periods[index]) ?? SUMMARY_TABLE_HIDDEN);
    }
  }

  if (hasRelativeSummaryOps(summary)) {
    // A relative edit rearranges what is already there, so it runs over the
    // worksheet's own values when we have them.
    after = applyRelativeSlotEdit(
      after ?? before,
      SUMMARY_TABLE_HIDDEN,
      {
        swapPeriods: summary.swapPeriods ?? undefined,
        removePeriods: summary.removePeriods ?? undefined,
        compactPeriods: summary.compactPeriods ?? undefined,
      },
      (slot) => !slotIsOccupied(slot),
    );
  }

  const slots: SummaryTableSlotValue[] = after ? [...after] : [...before];
  for (let index = 1; index <= SUMMARY_TABLE_PERIOD_SLOTS; index += 1) {
    // Explicit slot assignments are applied last so they win over an array or
    // relative edit sent in the same call.
    const explicit = coercePeriod(
      summary[`period${index}` as keyof FactsetChartSummaryTable],
    );
    if (explicit) slots[index - 1] = explicit;
  }

  // Only slots the request actually moves are stated. The rest of `before` is
  // usually the documented band for the chart's range rather than periods we
  // have seen, and sending those as if they were asked for would pin down every
  // slot Chart Creator is free to choose.
  slots.forEach((slot, index) => {
    if (slotComparable(slot) === slotComparable(before[index])) return;
    result[`summaryTable.period${index + 1}`] = slot;
  });
  return result;
}

/**
 * Collect `summaryTable.*` entries back into the API's nested shape.
 *
 * Slots nobody asked about are re-stated only from a table we actually saw, so
 * the request carries the absolute five-slot contract the API expects. A table
 * we merely inferred — the documented band for the chart's range, or the blanks
 * standing in for cells we could not read — is not restated: sending it would
 * order Chart Creator to hide every period it would otherwise have chosen.
 */
function summaryTableFromUpdates(
  updates: Record<string, unknown>,
  currentState: SummaryTableState,
): FactsetChartSummaryTable | undefined {
  const keys = Object.keys(updates).filter((key) =>
    key.startsWith("summaryTable."),
  );
  if (keys.length === 0) return undefined;
  const table: FactsetChartSummaryTable = {
    display: coerceDisplay(updates["summaryTable.display"]) ?? currentState.display,
  };
  for (let index = 1; index <= SUMMARY_TABLE_PERIOD_SLOTS; index += 1) {
    const period =
      coercePeriod(updates[`summaryTable.period${index}`]) ??
      (summaryTableObserved(currentState)
        ? currentState.periods[index - 1]
        : undefined);
    if (period) {
      table[`period${index}` as keyof FactsetChartSummaryTable] =
        period as never;
    }
  }
  return table;
}

/**
 * Whether this state was read from the workbook or from the request that built
 * it, as opposed to inferred from the chart's date range. Only an observed
 * table may be reported as the chart's own or restated back to the API as fact.
 */
function summaryTableObserved(state: SummaryTableState): boolean {
  return state.source === "worksheet" || state.source === "stored_parameters";
}

/** Deep-set a dotted parameter path on a cloned parameter object. */
function setByPath(
  target: Record<string, unknown>,
  path: string,
  value: unknown,
): void {
  const segments = path.split(".");
  let cursor = target;
  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    const existing = cursor[segment];
    const next =
      existing && typeof existing === "object" && !Array.isArray(existing)
        ? { ...(existing as Record<string, unknown>) }
        : {};
    cursor[segment] = next;
    cursor = next;
  }
  cursor[segments[segments.length - 1]] = value;
}

/**
 * Merge a flattened delta into the parameters the workbook was built from, so
 * an update re-requests the original chart with only the requested fields
 * changed instead of rebuilding it from whatever the model happened to repeat
 * back.
 */
function mergeParameterDelta(
  base: FactsetChartParameters,
  delta: Record<string, unknown>,
): FactsetChartParameters {
  const merged = JSON.parse(JSON.stringify(base)) as Record<string, unknown>;
  for (const [path, value] of Object.entries(delta)) {
    setByPath(merged, path, value);
  }
  return merged as unknown as FactsetChartParameters;
}

function currentChartType(
  inputMap: NativeChartInputMap,
  fallback?: string,
): string | undefined {
  const observed = inputMap.inputs.chartType?.currentValue;
  return typeof observed === "string" && observed.trim()
    ? observed.trim()
    : fallback?.trim() || undefined;
}

/** Why this chart cannot carry a summary table, when it cannot. */
function summarySupportWarning(
  chartType: string | undefined,
): string | undefined {
  if (!chartType) {
    return "The summary table was left out because the chart type could not be determined.";
  }
  const capability = findChartCapability(chartType);
  if (!capability?.summaryTable) {
    return `Summary tables are not supported for chart type "${chartType}", so the requested table was left out.`;
  }
  return undefined;
}

/**
 * Resolve a summary-table request into absolute `summaryTable.*` parameter
 * updates, alongside whatever else the caller is changing.
 *
 * Both entry points come through here, so a table asked for on a create and one
 * asked for on a follow-up are decided the same way and the download pipeline
 * itself holds no summary logic. A request that cannot be honoured drops out
 * with a warning rather than failing the operation: a chart that exists with
 * Chart Creator's own table beats no chart at all.
 */
function resolveSummaryUpdates(
  requested: Record<string, unknown>,
  summary: NativeChartToolInput["summaryTable"],
  chartType: string | undefined,
  currentState: SummaryTableState,
): { updates: Record<string, unknown>; warning?: string } {
  const updates = { ...requested };
  // A `summaryTable` object sent through the generic `updates` bag has to face
  // the same checks as the dedicated field, or it becomes a way around them.
  const requestedKeys = Object.keys(updates).filter((key) =>
    key.startsWith("summaryTable."),
  );
  if (!summary && requestedKeys.length === 0) return { updates };
  const withoutSummary = (
    warning: string,
  ): { updates: Record<string, unknown>; warning: string } => {
    for (const key of requestedKeys) delete updates[key];
    return { updates, warning };
  };

  const unsupported = summarySupportWarning(chartType);
  if (unsupported) return withoutSummary(unsupported);
  if (!summary) return { updates };
  const slotError = summarySlotIndexError(summary);
  if (slotError) return withoutSummary(slotError);
  // A relative request rearranges the table that is already there. With no
  // readable cells and no documented band for the chart's range there is
  // nothing to rearrange, and a guessed table is worse than none.
  if (hasRelativeSummaryOps(summary) && currentState.source === "unknown") {
    return withoutSummary(
      "The summary table could not be rearranged because this chart's current periods could not be determined.",
    );
  }
  return { updates: { ...updates, ...summaryUpdates(summary, currentState) } };
}

function inputMapError(inputMap: NativeChartInputMap): string | undefined {
  if (inputMap.truncatedSheets.length > 0) {
    return `Input discovery was truncated on: ${inputMap.truncatedSheets.join(", ")}.`;
  }
  if (inputMap.ambiguousInputs.length > 0) {
    return (
      "Chart Creator worksheet inputs are ambiguous: " +
      inputMap.ambiguousInputs.map((entry) => entry.parameter).join(", ")
    );
  }
  if (Object.keys(inputMap.inputs).length === 0) {
    const namedObjectDetail =
      inputMap.namedObjectDiscovery.status === "completed"
        ? ""
        : ` Named-object discovery ${inputMap.namedObjectDiscovery.status}${
            inputMap.namedObjectDiscovery.error
              ? `: ${inputMap.namedObjectDiscovery.error}`
              : "."
          }`;
    return (
      "No Chart Creator worksheet inputs could be discovered from names or visible labels." +
      namedObjectDetail
    );
  }
  return undefined;
}

/**
 * Workbook names arrive from several hosts in slightly different shapes (bare
 * name, name with extension, occasionally a full path). Compare on the file
 * name without extension so an activation check does not fail on cosmetics.
 */
function sameWorkbook(left: string | undefined, right: string | undefined): boolean {
  const normalize = (value: string | undefined): string =>
    (value ?? "")
      .trim()
      .replace(/^.*[\\/]/, "")
      .replace(/\.(xlsx|xlsm|xlsb|xls|xltx|xltm)$/i, "")
      .toLowerCase();
  const a = normalize(left);
  const b = normalize(right);
  return a.length > 0 && a === b;
}

/**
 * Bring the target workbook to the foreground and confirm it, retrying the
 * activation a bounded number of times. Refresh is active-workbook-only, so
 * dispatching it against the wrong active workbook silently refreshes someone
 * else's data — this must be an assertion, not a hope.
 */
async function assertTargetWorkbookActive(
  dependencies: NativeChartDependencies,
  workbookName: string,
  sheetName: string | undefined,
): Promise<void> {
  let observed: string | undefined;
  for (
    let attempt = 1;
    attempt <= NATIVE_CHART_ACTIVATION_ATTEMPTS;
    attempt += 1
  ) {
    try {
      observed = (await dependencies.getActiveContext()).workbookName?.trim();
    } catch (error) {
      observed = undefined;
      if (attempt === NATIVE_CHART_ACTIVATION_ATTEMPTS) {
        throw new Error(
          `Could not confirm the active workbook before refreshing "${workbookName}": ${errorText(error)}`,
        );
      }
    }
    if (sameWorkbook(observed, workbookName)) return;
    if (attempt === NATIVE_CHART_ACTIVATION_ATTEMPTS) break;
    if (sheetName) {
      try {
        await dependencies.activateSheet(sheetName, workbookName);
      } catch {
        // Best-effort; the loop re-reads the active context either way.
      }
    }
    await dependencies.sleep(NATIVE_CHART_ACTIVATION_RETRY_DELAY_MS);
  }
  throw new Error(
    `Refusing to refresh: Excel's active workbook is "${observed || "(unknown)"}" but this run targets "${workbookName}".`,
  );
}

type NativeChartRefreshStatus = "completed" | "in_progress";

interface NativeChartRefreshOutcome {
  status: NativeChartRefreshStatus;
  refresh: Record<string, unknown>;
  recalculate: Record<string, unknown> | null;
}

async function fullyRecalculate(
  ctx: ExecutorToolRuntimeContext,
  workbookName: string,
  fallbackSheet: string | undefined,
): Promise<Record<string, unknown>> {
  if (!ctx.recalculateWorkbook) {
    throw new Error("Workbook recalculation is not available in this executor run.");
  }
  const recalculate = await ctx.recalculateWorkbook({
    mode: "full",
    pinnedWorkbookName: workbookName,
    fallbackSheet: fallbackSheet ?? ctx.session.sessionLockSheet,
  });
  if (!recalculate.ok || !recalculate.recalculated) {
    throw new Error(
      recalculate.error ||
        recalculate.message ||
        "Workbook recalculation did not complete.",
    );
  }
  return recalculate as unknown as Record<string, unknown>;
}

/**
 * Lead with the sheet the run is parked on. The host services
 * `HarmonizedRefreshWorksheets` in the foreground against the first sheet of
 * the list, so any other sheet there pulls the user's view away and drops it
 * back once the refresh returns.
 */
function focusSheetFirst(
  sheetNames: string[],
  focusSheetName: string | undefined,
): string[] {
  if (!focusSheetName) return sheetNames;
  const rest = sheetNames.filter((name) => name !== focusSheetName);
  if (rest.length === sheetNames.length) return sheetNames;
  return [focusSheetName, ...rest];
}

async function refreshAndRecalculate(
  ctx: ExecutorToolRuntimeContext,
  dependencies: NativeChartDependencies,
  workbookName: string,
  sheetNames: string[],
  focusSheetName?: string,
): Promise<NativeChartRefreshOutcome> {
  if (!ctx.refreshFactSetData) {
    throw new Error("FactSet harmonized refresh is not available in this executor run.");
  }
  const ordered = focusSheetFirst(sheetNames, focusSheetName);
  await assertTargetWorkbookActive(dependencies, workbookName, ordered[0]);
  const refresh = await ctx.refreshFactSetData({
    sheetNames: ordered,
    // HarmonizedRefreshWorksheets has no workbook field of its own. Keep the
    // imported worksheets explicit; the assertion above guarantees the pinned
    // target is the workbook the host will act on.
    refreshAll: false,
    pinnedWorkbookName: workbookName,
    fallbackSheet: ordered[0] ?? ctx.session.sessionLockSheet,
  });
  // A soft transport timeout does not cancel the host RPC — the refresh is
  // still running behind the host's own progress dialog. Reporting that as a
  // failure would tell the user to retry work that is already happening.
  if (!refresh.ok && isIntentTimeoutError(refresh.error ?? refresh.message)) {
    return {
      status: "in_progress",
      refresh: refresh as unknown as Record<string, unknown>,
      recalculate: null,
    };
  }
  const expected = new Set(sheetNames.map((name) => name.toLowerCase()));
  const refreshed = new Set(
    refresh.refreshedSheetNames.map((name) => name.toLowerCase()),
  );
  if (
    !refresh.ok ||
    !refresh.refreshed ||
    !refresh.scopeConfirmed ||
    Array.from(expected).some((name) => !refreshed.has(name))
  ) {
    throw new Error(
      refresh.error ||
        refresh.message ||
        "FactSet refresh did not confirm every Chart Creator worksheet.",
    );
  }
  return {
    status: "completed",
    refresh: refresh as unknown as Record<string, unknown>,
    recalculate: await fullyRecalculate(ctx, workbookName, ordered[0]),
  };
}

async function downloadChartWorkbook(
  dependencies: NativeChartDependencies,
  parameters: FactsetChartParameters,
  signal: AbortSignal | undefined,
): Promise<{ mode: ChartCreatorModeResolution["mode"]; base64: string }> {
  // `parameters.summaryTable` is serialized by the payload layer in both API
  // and legacy mode; nothing about the table is decided here.
  const mode = await dependencies.resolveMode();
  let download: FactsetDownloadExcelChart;
  if (mode.mode === "api") {
    try {
      download = await dependencies.downloadViaApi(parameters, { signal });
    } catch (error) {
      if (
        !(error instanceof Error) ||
        error.name !== "ChartCreatorReauthRequiredError"
      ) {
        throw error;
      }
      await dependencies.reauthorize();
      download = await dependencies.downloadViaApi(parameters, { signal });
    }
  } else {
    download = await dependencies.downloadViaLegacy(parameters, { signal });
  }
  const base64 = download.workbookBase64?.trim();
  if (!base64) {
    throw new Error(download.error || "Chart Creator returned no workbook bytes.");
  }
  return { mode: mode.mode, base64 };
}

async function openAndVerifyWorkbook(
  ctx: ExecutorToolRuntimeContext,
  dependencies: NativeChartDependencies,
  base64: string,
  expectedChartType: string,
  signal?: AbortSignal,
): Promise<{
  workbookName: string;
  hostReady: PatientHostReadyResult;
  charts: ChartingChartInfo[];
  sheetNames: string[];
  finalRefresh: NativeChartRefreshOutcome;
  lifecycle: Record<string, unknown>;
}> {
  const lifecycle: Record<string, unknown> = {
    workbookActivated: false,
    workbookRepinned: false,
    chatWorkbookHandoffCompleted: false,
    calcModeBracketed: false,
    calcModeRestored: false,
    refreshAttempted: false,
    refreshCompleted: false,
    recalculationCompleted: false,
    nativeChartStructureVerified: false,
    populatedValuesVerified: false,
    nativeChartVerified: false,
  };
  let originalCalculationMode: CalculationMode | null = null;
  let calculationModeFlipped = false;
  let calculationModeRestored = false;
  let workbookName: string | undefined;
  let insertedSheetNames: string[] = [];
  let releaseConversationHold: (() => void) | undefined;

  const lifecycleSnapshot = (): Record<string, unknown> => ({
    ...lifecycle,
    sourceWorkbookName:
      typeof lifecycle.sourceWorkbookName === "string"
        ? lifecycle.sourceWorkbookName
        : null,
    targetWorkbookName: workbookName ?? null,
    insertedSheetNames: insertedSheetNames.slice(),
  });
  const restoreCalculationMode = async (): Promise<unknown | undefined> => {
    if (!calculationModeFlipped || calculationModeRestored) return undefined;
    lifecycle.calcModeRestoreAttempted = true;
    const target = originalCalculationMode ?? "automatic";
    try {
      await dependencies.setCalculationMode(target);
      calculationModeRestored = true;
      lifecycle.calcModeRestored = true;
      lifecycle.calcModeRestoreTarget = target;
      delete lifecycle.calcModeRestoreError;
      return undefined;
    } catch (error) {
      lifecycle.calcModeRestoreError = errorText(error);
      return error;
    }
  };
  const recoverCreatedWorkbook = async (): Promise<void> => {
    await restoreCalculationMode();
    if (
      !workbookName ||
      insertedSheetNames.length === 0 ||
      lifecycle.refreshAttempted === true
    ) {
      return;
    }
    lifecycle.recoveryRefreshAttempted = true;
    try {
      await refreshAndRecalculate(
        ctx,
        dependencies,
        workbookName,
        insertedSheetNames,
      );
      lifecycle.recoveryRefreshCompleted = true;
    } catch (error) {
      lifecycle.recoveryRefreshCompleted = false;
      lifecycle.recoveryRefreshError = errorText(error);
    }
  };

  try {
    await requireHostReady(dependencies, signal);
  } catch (error) {
    throw phaseError(
      "pre-create host readiness",
      error,
      lifecycleSnapshot(),
    );
  }
  let before: ActiveExcelContext;
  try {
    before = await dependencies.getActiveContext();
    lifecycle.sourceWorkbookName = before.workbookName?.trim() || null;
  } catch (error) {
    throw phaseError(
      "source workbook context",
      error,
      lifecycleSnapshot(),
    );
  }

  try {
    try {
      originalCalculationMode = await dependencies.getCalculationMode();
      lifecycle.originalCalculationMode = originalCalculationMode;
    } catch (error) {
      lifecycle.calcModeReadError = errorText(error);
    }
    if (originalCalculationMode === "manual") {
      lifecycle.calcModeBracketed = true;
      lifecycle.calcModePreserved = true;
      lifecycle.calcModeRestored = true;
    } else {
      try {
        await dependencies.setCalculationMode("manual");
        calculationModeFlipped = true;
        lifecycle.calcModeBracketed = true;
      } catch (error) {
        lifecycle.calcModeBracketError = errorText(error);
      }
    }

    // Excel activates the new workbook as soon as AddWorkbook returns, but the
    // conversation can only move once the sheets are in it. Hold the task
    // pane on this chat until then so the user sees one transition instead of
    // losing the conversation to an empty workbook and getting it back.
    releaseConversationHold = ctx.beginWorkbookHandoff?.();

    try {
      await dependencies.createBlankWorkbook();
    } catch (error) {
      throw phaseError(
        "blank workbook creation (AddWorkbook)",
        error,
        lifecycleSnapshot(),
      );
    }
    let active: ActiveExcelContext;
    try {
      active = await waitForWorkbookSwitch(
        dependencies,
        before.workbookName,
        signal,
      );
    } catch (error) {
      throw phaseError(
        "blank workbook activation",
        error,
        lifecycleSnapshot(),
      );
    }
    workbookName = active.workbookName?.trim();
    if (!workbookName) {
      throw phaseError(
        "blank workbook activation",
        "AddWorkbook activated a workbook without a name.",
        lifecycleSnapshot(),
      );
    }

    // Re-pin the host target now; the conversation follows once the sheets are
    // in and the workbook has something to show.
    ctx.session.pinnedWorkbookName = workbookName;
    lifecycle.workbookActivated = true;
    lifecycle.workbookRepinned = true;

    try {
      insertedSheetNames = await dependencies.insertDownloadedWorkbook(
        base64,
        workbookName,
      );
      lifecycle.workbookInserted = true;
    } catch (error) {
      throw phaseError(
        "XLSX insertion (InsertWorksheetsFromBase64)",
        error,
        lifecycleSnapshot(),
      );
    }

    // InsertWorksheetsFromBase64 is the authoritative source of the sheets
    // inserted into this new workbook. Creation intentionally does not list
    // worksheets or inspect used ranges/names merely to rediscover them.
    const sheetNames = insertedSheetNames.slice();
    if (sheetNames.length === 0) {
      throw phaseError(
        "XLSX insertion (InsertWorksheetsFromBase64)",
        "The host did not report any inserted worksheets to refresh.",
        lifecycleSnapshot(),
      );
    }

    // Structural writes occasionally leave the host's cell-edit flag set with
    // no edit actually in flight, which then traps the next intent.
    try {
      lifecycle.editModeDrainResult = await dependencies.drainEditMode();
    } catch (error) {
      lifecycle.editModeDrainError = errorText(error);
    }

    let hostReady: PatientHostReadyResult;
    try {
      hostReady = await requireHostReady(dependencies, signal);
      lifecycle.postInsertionHostReady = true;
    } catch (error) {
      throw phaseError(
        "post-insertion host readiness",
        error,
        lifecycleSnapshot(),
      );
    }

    // Chart Creator names the sheet holding the chart after the chart type.
    const chartSheetName = sheetNames.find(
      (name) => name.toLowerCase() === expectedChartType.toLowerCase(),
    );
    // Park on the chart before handing the conversation over. Where the host
    // leaves the view after an import is its own business — often the blank
    // starter sheet AddWorkbook created — and every foreground refresh below
    // opens on the sheet it is given and then returns the view to wherever it
    // found it. Settling here is what keeps those refreshes invisible.
    const landingSheetName = chartSheetName ?? sheetNames[0];
    try {
      await dependencies.activateSheet(landingSheetName, workbookName);
      lifecycle.landingSheetName = landingSheetName;
    } catch (error) {
      lifecycle.landingSheetError = errorText(error);
    }
    resetWorkbookScopedRunState(ctx, landingSheetName);

    try {
      if (!ctx.onWorkbookHandoff) {
        throw new Error(
          "The executor did not provide a conversation/workbook handoff callback.",
        );
      }
      await ctx.onWorkbookHandoff({
        sourceWorkbookName: before.workbookName?.trim(),
        targetWorkbookName: workbookName,
        activeSheetName: landingSheetName,
        reason: "native_chart",
      });
      lifecycle.chatWorkbookHandoffCompleted = true;
    } catch (error) {
      throw phaseError(
        "conversation/workbook handoff",
        error,
        lifecycleSnapshot(),
      );
    }
    // The chat now lives in the new workbook, so the pane can follow Excel
    // again.
    releaseConversationHold?.();
    releaseConversationHold = undefined;
    try {
      ctx.onPlanStateChange(ctx.planState);
    } catch (error) {
      throw phaseError(
        "target-workbook plan handoff",
        error,
        lifecycleSnapshot(),
      );
    }

    let restoreError = await restoreCalculationMode();
    if (restoreError) {
      // A transient host transition can reject the first restore directly
      // after insertion. Retry once before refresh, then still refresh/recalc
      // explicitly so the workbook is responsive even if mode restore fails.
      restoreError = await restoreCalculationMode();
    }

    let charts: ChartingChartInfo[] = [];
    let finalRefresh: NativeChartRefreshOutcome | undefined;
    for (
      let attempt = 1;
      attempt <= NATIVE_CHART_REFRESH_ATTEMPTS;
      attempt += 1
    ) {
      lifecycle.refreshAttempted = true;
      lifecycle.refreshAttempts = attempt;
      try {
        // Refresh is deliberately the final mutation in the create lifecycle:
        // workbook creation, insertion, handoff, and calc restoration are
        // already complete at this point.
        finalRefresh = await refreshAndRecalculate(
          ctx,
          dependencies,
          workbookName,
          sheetNames,
          landingSheetName,
        );
        lifecycle.refreshStatus = finalRefresh.status;
        lifecycle.refreshCompleted = finalRefresh.status === "completed";
        lifecycle.recalculationCompleted = finalRefresh.recalculate !== null;
      } catch (error) {
        throw phaseError(
          "final target-workbook refresh/recalculation",
          error,
          lifecycleSnapshot(),
        );
      }
      if (finalRefresh.status === "in_progress") {
        // The host is still fetching behind its own progress dialog. Querying
        // charts now only queues more foreground work behind that refresh.
        lifecycle.chartVerificationDeferred = true;
        break;
      }
      try {
        await requireHostReady(dependencies, signal);
        charts = await verifyNativeCharts(
          dependencies,
          workbookName,
          chartSheetName,
          NATIVE_CHART_CREATE_VERIFY_TIMEOUT_MS,
        );
      } catch (error) {
        if (isIntentTimeoutError(error)) {
          // Insert and refresh have both completed and the workbook is already
          // visible. A metadata timeout must not turn that finished mutation
          // into a failure or trigger another chart-read loop.
          lifecycle.chartVerificationTimedOut = true;
          lifecycle.chartVerificationError = errorText(error);
          break;
        }
        throw phaseError(
          "post-refresh verification (GetCharts)",
          error,
          lifecycleSnapshot(),
        );
      }
      if (charts.length === 0) {
        throw phaseError(
          "post-refresh verification (GetCharts)",
          "The downloaded workbook was refreshed, but GetCharts did not confirm a native Excel chart.",
          lifecycleSnapshot(),
        );
      }
      lifecycle.nativeChartStructureVerified = true;
      if (!hasCalculationPlaceholder(charts)) {
        lifecycle.populatedValuesVerified = true;
        break;
      }
      lifecycle.calculationPlaceholderObserved = true;
      if (attempt < NATIVE_CHART_REFRESH_ATTEMPTS) {
        await dependencies.sleep(NATIVE_CHART_REFRESH_RETRY_DELAY_MS);
      }
    }
    if (!finalRefresh) {
      throw phaseError(
        "final target-workbook refresh/recalculation",
        "The target workbook refresh did not return a result.",
        lifecycleSnapshot(),
      );
    }
    // A completed host refresh can occasionally take another UI cycle before
    // GetCharts stops echoing a cached #CALC series label. The workbook and
    // chart are already valid, so do not turn that transient observation into
    // a user-facing create failure after the bounded retry; the caller reports
    // it separately as a data-population warning.
    lifecycle.nativeChartVerified = lifecycle.nativeChartStructureVerified === true;

    // Last chance to put the calculation mode back: the host is idle again now
    // that the refresh has finished, so an attempt that failed mid-import can
    // succeed here. A workbook this far along is not worth failing over if it
    // does not — the caller reports the leftover manual mode as a warning.
    if (restoreError) await restoreCalculationMode();

    return {
      workbookName,
      hostReady,
      charts,
      sheetNames,
      finalRefresh,
      lifecycle: lifecycleSnapshot(),
    };
  } catch (error) {
    await recoverCreatedWorkbook();
    if (error instanceof NativeChartLifecycleError) {
      throw new NativeChartLifecycleError(error.message, {
        ...error.details,
        ...lifecycleSnapshot(),
        phase: error.details.phase,
      });
    }
    throw phaseError(
      "workbook lifecycle",
      error,
      lifecycleSnapshot(),
    );
  } finally {
    // A lifecycle that never reached the handoff must not leave the task pane
    // frozen on a workbook the user has already been moved away from.
    releaseConversationHold?.();
    await restoreCalculationMode();
  }
}

const IDENTIFIER_TABLE_PROBE_MAX_ROWS = 160;
const IDENTIFIER_TABLE_PROBE_MAX_COLUMNS = 32;

interface NativeChartIdentifierSlot {
  index: number;
  tickerAddress: string;
  nameAddress: string;
  tickerValue: string | number | boolean | null;
  nameValue: string | number | boolean | null;
}

interface NativeChartIdentifierTable {
  sheetName: string;
  slots: NativeChartIdentifierSlot[];
}

const emptyNativeChartInputMap = (): NativeChartInputMap => ({
  inputs: {},
  ambiguousInputs: [],
  truncatedSheets: [],
  namedObjectDiscovery: { status: "completed" },
});

function identifierSlotIndex(value: unknown): number | undefined {
  if (typeof value !== "string") return undefined;
  const words = observedNameWords(value);
  if (words.length !== 2 || words[0] !== "identifier") return undefined;
  const index = Number(words[1]);
  return Number.isInteger(index) && index > 0 ? index : undefined;
}

function isIdentifierHeader(value: unknown): boolean {
  return (
    typeof value === "string" &&
    observedNameWords(value).join("") === "identifier"
  );
}

function isCompanyNameHeader(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const words = observedNameWords(value);
  return (
    (words.length === 1 && words[0] === "name") ||
    (words.length === 2 && words[0] === "company" && words[1] === "name")
  );
}

/**
 * Recognize the template's repeated identifier table from its headers and
 * consecutive numbered slot rows. No workbook-specific address is assumed.
 */
function detectIdentifierTable(
  sheetName: string,
  read: ReadRangeContentResult,
): NativeChartIdentifierTable | undefined {
  const range = read.range;
  if (!range) return undefined;
  for (let headerRow = 0; headerRow < range.values.length - 1; headerRow += 1) {
    const header = range.values[headerRow] ?? [];
    const tickerColumn = header.findIndex(isIdentifierHeader);
    const nameColumn = header.findIndex(isCompanyNameHeader);
    if (tickerColumn < 0 || nameColumn < 0 || tickerColumn === nameColumn) {
      continue;
    }
    const firstDataRow = range.values[headerRow + 1] ?? [];
    const labelColumn = firstDataRow.findIndex(
      (value) => identifierSlotIndex(value) === 1,
    );
    if (labelColumn < 0) continue;

    const slots: NativeChartIdentifierSlot[] = [];
    for (
      let row = headerRow + 1, expectedIndex = 1;
      row < range.values.length;
      row += 1, expectedIndex += 1
    ) {
      const values = range.values[row] ?? [];
      if (identifierSlotIndex(values[labelColumn]) !== expectedIndex) break;
      slots.push({
        index: expectedIndex,
        tickerAddress: cellAddress(
          range.startRow + row,
          range.startColumn + tickerColumn,
        ),
        nameAddress: cellAddress(
          range.startRow + row,
          range.startColumn + nameColumn,
        ),
        tickerValue: values[tickerColumn] ?? null,
        nameValue: values[nameColumn] ?? null,
      });
    }
    if (slots.length > 0) return { sheetName, slots };
  }
  return undefined;
}

async function discoverIdentifierTable(
  workbookName: string,
  dependencies: Pick<NativeChartDependencies, "listSheets" | "readRange">,
): Promise<{
  sheets: WorksheetMetadata[];
  table?: NativeChartIdentifierTable;
}> {
  const sheets = await dependencies.listSheets(workbookName);
  for (const sheet of sheets) {
    if (
      (sheet.visibility && sheet.visibility !== "Visible") ||
      !sheet.usedRange
    ) {
      continue;
    }
    const address = boundedRangeAddress(
      sheet.usedRange.address,
      IDENTIFIER_TABLE_PROBE_MAX_ROWS,
      IDENTIFIER_TABLE_PROBE_MAX_COLUMNS,
    );
    if (!address) continue;
    const read = await dependencies.readRange(
      sheet.name,
      address,
      workbookName,
    );
    const table = detectIdentifierTable(sheet.name, read);
    if (table) return { sheets, table };
  }
  return { sheets };
}

/** What the workbook itself still shows about how it was requested. */
interface ObservedNativeChartWorkbook {
  sheets?: readonly WorksheetMetadata[];
  inputMap?: NativeChartInputMap;
  identifiers?: NativeChartIdentifierTable;
}

/**
 * The request that would produce this workbook again, read back out of the
 * workbook itself.
 *
 * What a chart was created from is only remembered while the task pane stays
 * open, so an edit made after a reload — or against a chart built in an
 * earlier session — has no baseline to re-request and would otherwise
 * dead-end on a chart that cannot absorb it. Every field here comes from the
 * template: the chart type from the worksheet Chart Creator names after it,
 * the companies from its identifier table, and the rest from its own input
 * block. Nothing is taken from the conversation.
 */
async function reconstructNativeChartParameters(
  workbookName: string,
  dependencies: NativeChartDependencies,
  observed: ObservedNativeChartWorkbook,
): Promise<FactsetChartParameters | undefined> {
  let sheets = observed.sheets;
  let identifiers = observed.identifiers;
  if (!identifiers) {
    const discovered = await discoverIdentifierTable(workbookName, dependencies);
    sheets = sheets ?? discovered.sheets;
    identifiers = discovered.table;
  }
  // Without the identifier table there is no way to say which companies the
  // chart is of, and a rebuild would silently change the subject.
  if (!identifiers || !sheets) return undefined;

  // Chart Creator names the worksheet after the chart it built.
  const chartType = sheets
    .map((sheet) => findChartCapability(sheet.name))
    .find((capability) => capability?.outputModes.includes("native_chart"))?.id;
  if (!chartType) return undefined;

  const tickers = identifiers.slots
    .filter((slot) => hasCellValue(slot.tickerValue))
    .map((slot) => String(slot.tickerValue).trim());
  if (tickers.length === 0) return undefined;
  // Company names are deliberately left out: FactSet resolves them from the
  // identifiers — it returned "Alphabet Inc. Class A" for a request that said
  // "Alphabet Inc." — and a template parks a placeholder in the name cells of
  // the slots it is not using.
  const parameters: FactsetChartParameters = { chartType, tickers };

  const inputs =
    observed.inputMap?.inputs ??
    (await resolveNativeChartInputs(workbookName, dependencies)).inputMap
      .inputs;
  for (const [parameter, location] of Object.entries(inputs)) {
    // The identifier table above is the authoritative list of companies. A
    // lone "Identifier" label in the input block would otherwise map the same
    // parameter to one cell and reduce the chart to a single company.
    if (parameter === "tickers" || parameter.startsWith("companyNames.")) {
      continue;
    }
    // The summary table is resolved from the worksheet separately, and a
    // parameter the download request has no field for cannot be carried.
    if (parameter.startsWith("summaryTable.")) continue;
    if (isWorksheetOnlyParameter(parameter)) continue;
    if (!hasCellValue(location.currentValue)) continue;
    setByPath(
      parameters as unknown as Record<string, unknown>,
      parameter,
      location.currentValue,
    );
  }
  try {
    assertChartParametersValid(parameters, "native_chart rebuild");
  } catch {
    return undefined;
  }
  return parameters;
}

/**
 * The chart to re-request: what this workbook was built from, with the
 * requested change merged in.
 */
function mergedRebuildParameters(
  baseline: FactsetChartParameters,
  updates: Record<string, unknown>,
  summaryTableBefore: SummaryTableState,
): { parameters: FactsetChartParameters } | { error: string } {
  const merged = mergeParameterDelta(baseline, updates);
  const mergedSummary = summaryTableFromUpdates(updates, summaryTableBefore);
  if (mergedSummary) merged.summaryTable = mergedSummary;
  const capability = findChartCapability(merged.chartType ?? "");
  if (!capability || !capability.outputModes.includes("native_chart")) {
    return {
      error: `Chart type "${merged.chartType ?? ""}" is not available in native_chart mode.`,
    };
  }
  return { parameters: merged };
}

/**
 * Ask Chart Creator for the workbook these parameters describe, open it,
 * refresh it, and report what the host confirmed.
 *
 * Creating a chart and changing one are the same operation: Chart Creator
 * builds a chart on request only, so a different chart is a different request.
 * `operation` only labels the step and its evidence. Both entry points record
 * the workbook as this run's completed build, so a redundant
 * `read_native_chart` is skipped after an update exactly as after a create.
 */
async function buildNativeChartWorkbook(
  ctx: ExecutorToolRuntimeContext,
  dependencies: NativeChartDependencies,
  args: {
    operation: "create_native_chart" | "update_native_chart";
    stepId: string;
    parameters: FactsetChartParameters;
    /** What the caller had to settle for, for the user-facing step note. */
    warnings?: readonly string[];
    /** How the caller arrived at these parameters. */
    evidence?: Record<string, unknown>;
    note?: string;
    signal?: AbortSignal;
  },
): Promise<string> {
  const { operation, stepId, parameters } = args;
  ctx.session.nativeChartCreateCompletedWorkbookName = undefined;
  ctx.session.nativeChartCreateVerifiedWorkbookName = undefined;
  let partialMutation = false;
  let failureDetails: Record<string, unknown> | undefined;
  try {
    const { mode, base64 } = await downloadChartWorkbook(
      dependencies,
      parameters,
      args.signal,
    );
    ctx.accumulators.workbookEditAttempted = true;
    partialMutation = true;
    const opened = await openAndVerifyWorkbook(
      ctx,
      dependencies,
      base64,
      parameters.chartType ?? "",
      args.signal,
    );
    failureDetails = opened.lifecycle;
    // The workbook is open and refreshed. Everything below only refines that
    // result, so record completion now: a later optional host read must not
    // discard the workbook or trigger a same-run retry loop.
    ctx.session.nativeChartCreateCompletedWorkbookName = opened.workbookName;
    if (opened.lifecycle.nativeChartVerified === true) {
      ctx.session.nativeChartCreateVerifiedWorkbookName = opened.workbookName;
    }
    // Remembering the request is what lets the next turn rebuild from it
    // without reading a single cell.
    dependencies.records.set(opened.workbookName, {
      parameters,
      sheetNames: opened.sheetNames.slice(),
      createdAt: Date.now(),
    });

    // Only the request is reported. This operation never reads the summary-table
    // cells, so what the workbook actually ended up with is not ours to state.
    const summaryTable = {
      applicable: Boolean(
        findChartCapability(parameters.chartType ?? "")?.summaryTable,
      ),
      configurationSource: parameters.summaryTable
        ? "download_request"
        : "not_requested",
      requested: parameters.summaryTable ?? null,
      message: parameters.summaryTable
        ? "The summary table was stated on the Chart Creator download request. FactSet is still rolling out support for that field, so tell the user it was requested rather than confirming it arrived."
        : "No summary table was requested, so this workbook carries whatever its template provides.",
    };

    // This stays a download/open/refresh operation. A sampled cell read was
    // both redundant with the post-refresh chart check and prone to a false
    // negative while formulas were still settling.
    const dataPopulationPending =
      opened.lifecycle.calculationPlaceholderObserved === true ||
      opened.lifecycle.refreshStatus === "in_progress";
    const dataPopulation = {
      dataPopulated: dataPopulationPending
        ? false
        : opened.lifecycle.populatedValuesVerified === true
          ? true
          : null,
      calculationPlaceholderObserved:
        opened.lifecycle.calculationPlaceholderObserved === true,
      verificationSource: "post_refresh_chart_metadata",
    };
    const completionEvidence = {
      satisfied: true,
      operation,
      ...(args.evidence ?? {}),
      xlsxBytesReceived: true,
      ...opened.lifecycle,
      sheetsInserted: opened.sheetNames.length > 0,
      nativeChartVerified: opened.lifecycle.nativeChartVerified === true,
      // Reported alongside, never part of `satisfied`: the workbook is usable
      // even when Excel is still resolving the last formulas.
      dataPopulated: dataPopulation.dataPopulated,
    };
    const warnings = [
      ...(args.warnings ?? []),
      dataPopulation.dataPopulated === false
        ? "The native chart was created while Excel was still resolving its underlying values."
        : undefined,
      opened.lifecycle.calcModeBracketed === true &&
      opened.lifecycle.calcModeRestored !== true
        ? "Excel is still in manual calculation mode; set Formulas > Calculation Options back to Automatic."
        : undefined,
    ].filter((entry): entry is string => Boolean(entry));
    finishChartStoppingEvidence(ctx, {
      stepId,
      tool: "native_chart",
      operation,
      satisfied: true,
      details: completionEvidence,
      warning: warnings.length > 0 ? warnings.join(" ") : undefined,
    });
    return output({
      ok: true,
      applied: true,
      operation,
      stepId,
      mode,
      workbookName: opened.workbookName,
      hostReady: opened.hostReady,
      sheetNames: opened.sheetNames,
      charts: opened.charts,
      finalRefresh: opened.finalRefresh,
      lifecycle: opened.lifecycle,
      chartParameters: parameters,
      summaryTable,
      dataPopulated: dataPopulation,
      completionEvidence,
      ...(args.note ? { note: args.note } : {}),
      ...(warnings.length > 0 ? { warning: warnings.join(" ") } : {}),
      instruction:
        operation === "create_native_chart"
          ? "Creation is complete. Do not call read_native_chart for redundant verification. Use read_native_chart or update_native_chart only for an explicit follow-up inspection/edit."
          : "The rebuilt chart is open in this new workbook and the conversation has moved to it. Do not call read_native_chart for redundant verification.",
    });
  } catch (error) {
    return failure(ctx, {
      operation,
      stepId,
      error: error instanceof Error ? error.message : String(error),
      partialMutation,
      details:
        error instanceof NativeChartLifecycleError
          ? error.details
          : failureDetails,
    });
  }
}

async function executeCreate(
  ctx: ExecutorToolRuntimeContext,
  input: NativeChartToolInput,
  stepId: string,
  signal: AbortSignal | undefined,
  dependencies: NativeChartDependencies,
): Promise<string> {
  const parameters = input.chartParameters;
  if (
    !parameters ||
    !parameters.chartType?.trim() ||
    !Array.isArray(parameters.tickers) ||
    parameters.tickers.length === 0
  ) {
    return failure(ctx, {
      operation: "create_native_chart",
      stepId,
      error:
        "create_native_chart requires chartParameters.chartType and at least one ticker.",
    });
  }
  // Nothing is open yet, so a relative request ("swap L3M and L6M") is measured
  // against the table Chart Creator documents for this range rather than
  // against a workbook that does not exist.
  const summaryTableBefore = resolveSummaryTableState(
    emptyNativeChartInputMap(),
    parameters,
  );
  const summary = resolveSummaryUpdates(
    {},
    input.summaryTable,
    parameters.chartType,
    summaryTableBefore,
  );
  const merged = mergedRebuildParameters(
    parameters,
    summary.updates,
    summaryTableBefore,
  );
  if ("error" in merged) {
    return failure(ctx, {
      operation: "create_native_chart",
      stepId,
      error: merged.error,
    });
  }
  return buildNativeChartWorkbook(ctx, dependencies, {
    operation: "create_native_chart",
    stepId,
    parameters: merged.parameters,
    warnings: summary.warning ? [summary.warning] : [],
    evidence: {
      requestedSummaryTable: input.summaryTable ?? null,
      summaryTableBaseline: summaryTableBefore.source,
    },
    signal,
  });
}

async function executeRead(
  ctx: ExecutorToolRuntimeContext,
  dependencies: NativeChartDependencies,
): Promise<string> {
  try {
    const pinnedWorkbookName = ctx.session.pinnedWorkbookName?.trim();
    const createCompletedWorkbookName =
      ctx.session.nativeChartCreateCompletedWorkbookName?.trim();
    const createVerifiedWorkbookName =
      ctx.session.nativeChartCreateVerifiedWorkbookName?.trim();
    if (
      pinnedWorkbookName &&
      createCompletedWorkbookName === pinnedWorkbookName
    ) {
      const nativeChartVerified =
        createVerifiedWorkbookName === pinnedWorkbookName;
      return output({
        ok: true,
        applied: false,
        operation: "read_native_chart",
        workbookName: pinnedWorkbookName,
        skippedRedundantRead: true,
        instruction:
          "create_native_chart already inserted and refreshed this workbook in the current run. No additional host reads were performed.",
        completionEvidence: {
          satisfied: true,
          verificationSource: "create_native_chart",
          nativeChartVerified,
        },
      });
    }
    const workbookName = pinnedWorkbookName
      ? pinnedWorkbookName
      : (await dependencies.getActiveContext()).workbookName?.trim();
    if (!workbookName) {
      throw new Error("No active or pinned Chart Creator workbook is available.");
    }
    // A read reports what it found. Neither a chart Excel would not describe
    // nor an input block this template hides is a reason to refuse an answer,
    // and the rebuild an edit performs needs neither of them.
    let charts: ChartingChartInfo[] = [];
    let chartVerificationTimedOut = false;
    try {
      charts = await verifyNativeCharts(dependencies, workbookName);
    } catch (error) {
      if (!isIntentTimeoutError(error)) throw error;
      chartVerificationTimedOut = true;
    }
    const resolved = await resolveNativeChartInputs(workbookName, dependencies);
    const record = dependencies.records.get(workbookName);
    const discoveryError = inputMapError(resolved.inputMap);
    const summaryTable = resolveSummaryTableState(
      resolved.inputMap,
      record?.parameters,
    );
    const chartType = currentChartType(
      resolved.inputMap,
      record?.parameters.chartType,
    );
    const summaryApplicable = chartType
      ? Boolean(findChartCapability(chartType)?.summaryTable)
      : undefined;
    return output({
      ok: true,
      applied: false,
      operation: "read_native_chart",
      workbookName,
      sheetNames:
        resolved.sheets.length > 0
          ? resolved.sheets.map((sheet) => sheet.name)
          : (record?.sheetNames ?? []),
      charts,
      chartType,
      inputMap: resolved.inputMap,
      inputResolution: resolved.resolutionSource,
      templateFingerprint: resolved.fingerprint?.version ?? null,
      createdFromParameters: record?.parameters ?? null,
      inputDiscoveryWarning: discoveryError ?? null,
      chartVerificationWarning:
        charts.length > 0
          ? null
          : chartVerificationTimedOut
            ? "Excel did not return this workbook's chart metadata in time, so the chart itself could not be described."
            : "Excel did not report a native chart in this workbook.",
      // A table is reported only when it was read from the worksheet or stated
      // on the request that built the workbook. Otherwise it is unknown: the
      // documented band for the chart's range describes what the template is
      // meant to do, and reporting it here would present that as this chart's
      // table when the workbook may have none at all.
      summaryTable: summaryTableObserved(summaryTable)
        ? {
            applicable: summaryApplicable ?? null,
            display: summaryTable.display,
            periods: summaryTable.periods,
            source: summaryTable.source,
          }
        : {
            applicable: summaryApplicable ?? null,
            display: null,
            periods: null,
            source: "unknown",
            message:
              "This workbook's summary table could not be read, so whether it has one is unknown. Ask for the table you want rather than describing the one that is there.",
          },
      editability: {
        // Any change is a fresh Chart Creator request, so tell the model what
        // an edit costs before the user commits to one.
        changesRebuildInNewWorkbook: true,
        // With the request remembered the rebuild starts from it exactly;
        // without it, from what these worksheets still show.
        rebuildsFromRememberedRequest: Boolean(record),
      },
      completionEvidence: {
        satisfied: true,
        workbookActiveOrPinned: true,
        sheetsListed: resolved.sheets.length > 0,
        inputsUnambiguous: !discoveryError,
        nativeChartVerified: charts.length > 0,
      },
    });
  } catch (error) {
    return output({
      ok: false,
      applied: false,
      operation: "read_native_chart",
      error: error instanceof Error ? error.message : String(error),
      completionEvidence: { satisfied: false },
    });
  }
}

/**
 * Rebuild the chart with the requested change merged into the request it came
 * from.
 *
 * The chart itself is never touched: the previous workbook keeps the chart it
 * has, and the new one carries the change. All this function does is work out
 * which request to re-send — from the session's own record when it has one,
 * otherwise from what the worksheets still show — so the pipeline below is the
 * same one creation uses.
 */
async function executeUpdate(
  ctx: ExecutorToolRuntimeContext,
  input: NativeChartToolInput,
  stepId: string,
  dependencies: NativeChartDependencies,
): Promise<string> {
  try {
    const pinnedWorkbookName = ctx.session.pinnedWorkbookName?.trim();
    const workbookName = pinnedWorkbookName
      ? pinnedWorkbookName
      : (await dependencies.getActiveContext()).workbookName?.trim();
    if (!workbookName) {
      throw new Error("No active or pinned Chart Creator workbook is available.");
    }
    ctx.session.pinnedWorkbookName = workbookName;
    await requireHostReady(dependencies, ctx.signal);

    // The record is the whole request, exactly as it was sent, so a session
    // that still holds one reaches the download without a single host call.
    const record = dependencies.records.get(workbookName);
    let inputMap = emptyNativeChartInputMap();
    let baseline = record?.parameters;
    let baselineSource = "remembered_request";
    if (!baseline) {
      const resolved = await resolveNativeChartInputs(
        workbookName,
        dependencies,
      );
      inputMap = resolved.inputMap;
      baseline = await reconstructNativeChartParameters(
        workbookName,
        dependencies,
        { sheets: resolved.sheets, inputMap },
      );
      baselineSource = "reconstructed_from_workbook";
    }
    if (!baseline && input.chartParameters?.chartType?.trim()) {
      // A caller that restated the whole chart has told us what it is; asking
      // again would be the dead end.
      baseline = input.chartParameters;
      baselineSource = "restated_by_caller";
    }
    if (!baseline) {
      throw new Error(
        "The settings this chart was created from are not known to this session, and its worksheets do not show enough to rebuild them. Create the chart again with the settings you want.",
      );
    }

    const summaryTableBefore = resolveSummaryTableState(inputMap, baseline);
    const chartType =
      typeof input.updates?.chartType === "string"
        ? input.updates.chartType
        : currentChartType(inputMap, baseline.chartType);
    const summary = resolveSummaryUpdates(
      flattenObject(input.updates ?? {}),
      input.summaryTable,
      chartType,
      summaryTableBefore,
    );
    // Chart Creator has no request field for these, so a rebuild would drop
    // them. Everything else in the request still gets built.
    const dropped = Object.keys(summary.updates).filter(
      isWorksheetOnlyParameter,
    );
    const updates = { ...summary.updates };
    for (const parameter of dropped) delete updates[parameter];
    const droppedWarning =
      dropped.length > 0
        ? `${dropped.join(", ")} could not be changed: Chart Creator has no request field for ${dropped.length === 1 ? "it" : "them"}, so ${dropped.length === 1 ? "it was" : "they were"} left as ${dropped.length === 1 ? "it is" : "they are"}.`
        : undefined;
    const warnings = [summary.warning, droppedWarning].filter(
      (entry): entry is string => Boolean(entry),
    );

    if (Object.keys(updates).length === 0) {
      // Rebuilding would deliver a second workbook holding the same chart —
      // the disruption, not the fix.
      // A summary-table request can land here legitimately: the applicable chart
      // types are documented as arriving with a Horizontal table already in
      // place, so a request that names neither a layout nor any period has
      // nothing to override. Say so, including that the feature is still being
      // rolled out, rather than reporting a change that did not happen.
      const explanation =
        warnings.length > 0
          ? warnings.join(" ")
          : input.summaryTable &&
              findChartCapability(chartType ?? "")?.summaryTable
            ? "No layout or periods were named, and this chart type is documented as already including a Horizontal summary table, so there was nothing to request. FactSet is still rolling that feature out, so the table may not be in the workbook; the template's own summary-table control switches between Horizontal, Vertical and none."
            : "This update named nothing to change, so the chart was left as it is.";
      // The chart this step produced is still open and still verified, so the
      // step is finished — it just has nothing to show for this call. Reporting
      // it as unsatisfied would leave the model with no terminal state to move
      // to and no operation left that could earn one.
      finishChartStoppingEvidence(ctx, {
        stepId,
        tool: "native_chart",
        operation: "update_native_chart",
        satisfied: true,
        details: { satisfied: true, applied: false, reason: explanation, baselineSource },
        warning: explanation,
      });
      return output({
        ok: true,
        applied: false,
        operation: "update_native_chart",
        stepId,
        workbookName,
        warning: explanation,
        note: "Nothing was rebuilt, so this workbook is unchanged. Tell the user what was left alone instead of reporting a change.",
        completionEvidence: {
          satisfied: true,
          applied: false,
          reason: explanation,
          baselineSource,
        },
      });
    }

    const merged = mergedRebuildParameters(
      baseline,
      updates,
      summaryTableBefore,
    );
    if ("error" in merged) {
      return failure(ctx, {
        operation: "update_native_chart",
        stepId,
        error: merged.error,
      });
    }
    return buildNativeChartWorkbook(ctx, dependencies, {
      operation: "update_native_chart",
      stepId,
      parameters: merged.parameters,
      warnings,
      evidence: {
        previousWorkbookName: workbookName,
        baselineSource,
        requestedUpdates: updates,
        droppedParameters: dropped,
        summaryTableBefore,
      },
      note: "This chart was rebuilt from FactSet in a new workbook, which the conversation has moved to. The previous workbook still holds the chart as it was, and any manual formatting done there was not carried over.",
      signal: ctx.signal,
    });
  } catch (error) {
    return failure(ctx, {
      operation: "update_native_chart",
      stepId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function executeNativeChartTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  signal?: AbortSignal,
  dependencies: NativeChartDependencies = DEFAULT_NATIVE_CHART_DEPENDENCIES,
): Promise<string> {
  const input = (rawInput ?? {}) as NativeChartToolInput;
  const operation = input.operation;
  if (
    operation !== "create_native_chart" &&
    operation !== "read_native_chart" &&
    operation !== "update_native_chart"
  ) {
    return output({
      ok: false,
      applied: false,
      error:
        "native_chart.operation must be create_native_chart, read_native_chart, or update_native_chart.",
    });
  }
  if (operation === "read_native_chart") {
    return executeRead(ctx, dependencies);
  }
  const stepId = beginChartStoppingEvidence(ctx, {
    stepId: input.stepId,
    tool: "native_chart",
    operation,
  });
  if (!stepId) {
    return output({
      ok: false,
      applied: false,
      operation,
      error:
        `${operation} requires a valid stepId (or an unambiguous active chart step) before it can mutate a workbook.`,
      completionEvidence: { satisfied: false },
    });
  }
  if (operation === "create_native_chart") {
    return executeCreate(ctx, input, stepId, signal ?? ctx.signal, dependencies);
  }
  return executeUpdate(ctx, input, stepId, dependencies);
}
